import { createNetworkQuery } from '@/services/revenueApi/createNetworkQuery';
import useReleaseFlag from '@/hooks/useReleaseFlag';
import type { GenderPlaysResponse } from '@/types/api/gender';
import type { AgePlaysResponse } from '@/types/api/age';
import type { LocationPlaysResponse } from '@/types/api/location';
import type { ProfilesResponse } from '@/types/api/profile';
import type {
  PercentageListResponse,
  PlayerDownloadsMetricsResponse,
  PlayersResponse,
} from '@/types/api/shows';
import type { DeviceDownloadsResponse } from '@/types/api/devices';
import type { UniqueListenersResponse } from '@/types/api/unique-listeners';
import type { UniqueViewersAndListenersResponse } from '@/types/api/unique-viewers-and-listeners';
import type { UniquesTimeSeriesResponse } from '@/types/api/uniques';
import type { ChartGranularity } from '@/types';

/**
 * Same as `createNetworkQuery`, but sends `flag=audience_page_backfill` when that flag is on.
 * The query key includes additionalParams, so the two query versions don't share cache entries.
 * TODO: remove with the audience_page_backfill flag (swap back to `createNetworkQuery`).
 */
const createAudienceQuery = <TData, TAdditionalParams extends object = Record<string, unknown>>(
  queryKeyPrefix: string,
  buildPath: (networkId: string) => string
) => {
  const useNetworkQuery = createNetworkQuery<TData, TAdditionalParams & { flag?: string }>(
    queryKeyPrefix,
    buildPath
  );
  const useAudienceQuery: ReturnType<typeof createNetworkQuery<TData, TAdditionalParams>> = (
    networkId,
    from,
    to,
    options,
    additionalParams
  ) => {
    const flag = useReleaseFlag('audience_page_backfill');
    return useNetworkQuery(networkId, from, to, options, {
      ...additionalParams,
      ...(flag && { flag }),
    } as TAdditionalParams);
  };
  return useAudienceQuery;
};

export const useGenderPlaysQuery = createAudienceQuery<
  GenderPlaysResponse,
  { showIds?: string[]; sources?: string[] }
>('gender-plays', (networkId) => `/v2/networks/${networkId}/audience/gender-plays`);

export const useAgePlaysQuery = createAudienceQuery<
  AgePlaysResponse,
  { showIds?: string[]; sources?: string[] }
>('age-plays', (networkId) => `/v2/networks/${networkId}/audience/age-plays`);

export const useLocationPlaysQuery = createAudienceQuery<
  LocationPlaysResponse,
  { showIds?: string[]; sources?: string[]; excludeSocials?: boolean }
>('location-plays', (networkId) => `/v2/networks/${networkId}/audience/location-plays`);

export const useProfilesQuery = createNetworkQuery<
  ProfilesResponse,
  { showIds?: string[] }
>('profiles', (networkId) => `/v2/networks/${networkId}/audience/profiles`);

export const useTopPlayersQuery = createAudienceQuery<
  PlayersResponse,
  { showIds?: string[]; sources?: string[]; limit?: number; excludeSocials?: boolean }
>('top-players', (networkId) => `/v2/networks/${networkId}/audience/top-players`);

export const usePlayerDownloadsQuery = createAudienceQuery<
  PlayerDownloadsMetricsResponse,
  { showIds?: string[]; sources?: string[]; granularity?: ChartGranularity }
>('player-downloads', (networkId) => `/v2/networks/${networkId}/audience/player-downloads`);

export const useTopDevicesQuery = createAudienceQuery<
  PercentageListResponse,
  { showIds?: string[]; sources?: string[] }
>('devices', (networkId) => `/v2/networks/${networkId}/audience/top-devices`);

export const useDeviceDownloadsQuery = createAudienceQuery<
  DeviceDownloadsResponse,
  { showIds?: string[]; sources?: string[]; granularity?: ChartGranularity }
>('device-downloads', (networkId) => `/v2/networks/${networkId}/audience/device-downloads`);

export const useUniquesQuery = createNetworkQuery<
  UniquesTimeSeriesResponse,
  { showIds?: string[]; granularity?: ChartGranularity }
>('uniques', (networkId) => `/v2/networks/${networkId}/audience/uniques`);

export const useUniqueListenersQuery = createNetworkQuery<
  UniqueListenersResponse,
  { showIds?: string[] }
>(
  'unique-listeners',
  (networkId) => `/v2/networks/${networkId}/audience/unique-listeners`
);

export const useUniqueViewersAndListenersQuery = createAudienceQuery<
  UniqueViewersAndListenersResponse,
  { showIds?: string[] }
>(
  'unique-viewers-and-listeners',
  (networkId) => `/v2/networks/${networkId}/audience/unique-viewers-and-listeners`
);
